import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { tenantApiFetch } from './tenantApi';
import { useTenantAuth } from '../context/TenantAuthContext';

const STATUSES = ['active', 'inactive', 'archived'];

export default function TenantRoleFormPage() {
  const { roleId } = useParams();
  const isEdit = Boolean(roleId);
  const navigate = useNavigate();
  const { user, ready, hasPermission } = useTenantAuth();

  const [form, setForm] = useState({
    name: '',
    slug: '',
    description: '',
    status: 'active',
    permissionCodes: []
  });
  const [catalog, setCatalog] = useState([]);
  const [isSystem, setIsSystem] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (ready && !user) navigate('/tenant/login', { replace: true });
  }, [ready, user, navigate]);

  useEffect(() => {
    if (!ready || !user) return;
    if (!hasPermission('tenant.roles.manage')) navigate('/tenant/roles', { replace: true });
  }, [ready, user, hasPermission, navigate]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await tenantApiFetch('/api/tenant/permissions');
        const json = await res.json().catch(() => ({}));
        if (res.ok && !cancelled) setCatalog(json.permissions || []);
      } catch (_e) {
        /* ignore */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const load = useCallback(async () => {
    if (!isEdit) return;
    setLoading(true);
    setError(null);
    try {
      const res = await tenantApiFetch(`/api/tenant/roles/${roleId}`);
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Failed to load role');
      const r = json.role;
      setIsSystem(Boolean(r.is_system_role || r.is_system));
      setForm({
        name: r.role_name || r.name || '',
        slug: r.role_code || r.slug || '',
        description: r.description || '',
        status: r.status || 'active',
        permissionCodes: [...(r.permissions || [])]
      });
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [isEdit, roleId]);

  useEffect(() => {
    load();
  }, [load]);

  const togglePerm = (code) => {
    if (isSystem) return;
    setForm((f) => ({
      ...f,
      permissionCodes: f.permissionCodes.includes(code)
        ? f.permissionCodes.filter((c) => c !== code)
        : [...f.permissionCodes, code]
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const name = form.name.trim();
      const slug = form.slug.trim().toLowerCase().replace(/\s+/g, '-');
      if (!name) throw new Error('Role name is required');
      if (!isEdit && !slug) throw new Error('Role code is required');

      if (isEdit) {
        const body = {
          name,
          description: form.description.trim() || null
        };
        if (!isSystem) {
          body.slug = slug;
          body.status = form.status;
          body.permissionCodes = form.permissionCodes;
        }
        const res = await tenantApiFetch(`/api/tenant/roles/${roleId}`, {
          method: 'PATCH',
          body
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(json.error || 'Save failed');
        navigate(`/tenant/roles/${roleId}`);
      } else {
        const res = await tenantApiFetch('/api/tenant/roles', {
          method: 'POST',
          body: {
            name,
            slug,
            description: form.description.trim() || null,
            status: form.status,
            permissionCodes: form.permissionCodes
          }
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(json.error || 'Create failed');
        navigate(`/tenant/roles/${json.role.id}`);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {isEdit ? 'Edit role' : 'New role'}
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <Paper component="form" onSubmit={submit} sx={{ p: 3, maxWidth: 640 }}>
        <TextField
          label="Role name"
          required
          fullWidth
          margin="normal"
          value={form.name}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
        />
        <TextField
          label="Role code"
          required={!isEdit}
          fullWidth
          margin="normal"
          value={form.slug}
          onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
          disabled={isEdit && isSystem}
          helperText={
            isSystem
              ? 'System role code cannot be changed'
              : 'Lowercase identifier, unique per tenant (e.g. exam-coordinator)'
          }
        />
        <TextField
          label="Description"
          fullWidth
          margin="normal"
          multiline
          minRows={2}
          value={form.description}
          onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
        />
        {!isSystem && (
          <FormControl fullWidth margin="normal">
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={form.status}
              onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
            >
              {STATUSES.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {isSystem && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Status is always <strong>active</strong> for system roles.
          </Typography>
        )}

        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
          Permissions
          {isSystem && (
            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              (read-only)
            </Typography>
          )}
        </Typography>
        <FormGroup sx={{ maxHeight: 320, overflow: 'auto', border: 1, borderColor: 'divider', p: 1, borderRadius: 1 }}>
          {catalog.map((p) => (
            <FormControlLabel
              key={p.code}
              control={
                <Checkbox
                  checked={form.permissionCodes.includes(p.code)}
                  onChange={() => togglePerm(p.code)}
                  disabled={isSystem}
                />
              }
              label={
                <Box>
                    <Typography variant="body2">{p.permission_name || p.code}</Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {p.code}
                    </Typography>
                  {p.description && (
                    <Typography variant="caption" color="text.secondary">
                      {p.description}
                    </Typography>
                  )}
                </Box>
              }
            />
          ))}
        </FormGroup>

        <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
          <Button type="submit" variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={22} /> : 'Save'}
          </Button>
          <Button onClick={() => navigate(isEdit ? `/tenant/roles/${roleId}` : '/tenant/roles')}>
            Cancel
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}
